import React, { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "../../../ui/skeleton";
import { EmptyState, InsightCard, SectionHeading, SegmentedToggle } from "../components/Primitives";
import { BarList, ShareBar } from "../components/DataBars";
import { abbreviateNumber, countShort, dayMonth, monthShort, pct } from "../utils/format";
import { useMoney } from "../utils/currency";
import { deriveSeries } from "../utils/derive";

/**
 * Section A — Value & P&L (§4.2).
 *
 * Combo chart: stacked stake bars split casino vs sports in two shades of the
 * accent, with GGR overlaid as a line on its own axis. Money is operator-side
 * throughout (§2.1) — the green line rises when the player loses.
 */

const GRAINS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

/**
 * Recharts needs real colour values rather than class names, so the series read
 * the same ASTRA theme variables the Tailwind classes elsewhere compile to.
 * That keeps one source of truth and lets the chart follow light/dark.
 */
const CASINO_FILL = "hsl(var(--primary))";
const SPORTS_FILL = "hsl(var(--chart-sports))";
const GGR_STROKE = "hsl(var(--chart-positive))";
const AXIS_COLOR = "hsl(var(--muted-foreground))";
const GRID_COLOR = "hsl(var(--border))";

const axisLabel = (key, grain) => {
  if (grain === "monthly") return monthShort(key);
  return dayMonth(key);
};

function ChartTooltip({ active, payload, grain }) {
  const { moneyExact } = useMoney();
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;

  const rows = [
    { label: "Casino stake", value: moneyExact(point.casinoStake) },
    { label: "Sports stake", value: moneyExact(point.sportsStake) },
    { label: "Wins paid", value: moneyExact(point.wins) },
    { label: "GGR", value: moneyExact(point.ggr), accent: true },
    { label: "Hold", value: pct(point.hold, 1) },
  ];

  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-popover-foreground shadow-md">
      <div className="mb-1.5 text-sm font-medium">
        {grain === "weekly" ? `Week of ${axisLabel(point.key, grain)}` : axisLabel(point.key, grain)}
      </div>
      <dl className="space-y-1">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-6 text-xs">
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd className={`font-medium tabular-nums ${row.accent ? "text-chart-positive" : "text-foreground"}`}>
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function PnlChart({ series, grain }) {
  if (!series.length) {
    return <EmptyState minHeight={280} message="No settled activity in this period yet." />;
  }

  return (
    <div className="h-[288px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -8 }} barCategoryGap="28%">
          <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="key"
            tickFormatter={(key) => axisLabel(key, grain)}
            tick={{ fill: AXIS_COLOR, fontSize: 10 }}
            tickLine={false}
            axisLine={{ stroke: GRID_COLOR }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis
            yAxisId="stake"
            tickFormatter={abbreviateNumber}
            tick={{ fill: AXIS_COLOR, fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          <YAxis
            yAxisId="ggr"
            orientation="right"
            tickFormatter={abbreviateNumber}
            tick={{ fill: AXIS_COLOR, fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={44}
          />
          <Tooltip
            cursor={{ fill: "hsl(var(--muted-foreground) / 0.08)" }}
            content={<ChartTooltip grain={grain} />}
          />
          <Bar yAxisId="stake" dataKey="casinoStake" stackId="stake" fill={CASINO_FILL} maxBarSize={26} />
          <Bar
            yAxisId="stake"
            dataKey="sportsStake"
            stackId="stake"
            fill={SPORTS_FILL}
            maxBarSize={26}
            radius={[3, 3, 0, 0]}
          />
          <Line
            yAxisId="ggr"
            type="monotone"
            dataKey="ggr"
            stroke={GGR_STROKE}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-3 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-sm bg-primary" aria-hidden="true" />
        Casino stake
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-sm bg-chart-sports" aria-hidden="true" />
        Sports stake
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-0.5 w-4 rounded-full bg-chart-positive" aria-hidden="true" />
        GGR
      </span>
    </div>
  );
}

export function ValueAndPnl({ profile, kpis, loading }) {
  const [grain, setGrain] = useState("monthly");
  const { money, moneyExact } = useMoney();
  const series = useMemo(() => deriveSeries(profile, grain), [profile, grain]);

  const split = kpis
    ? [
        {
          label: "Casino",
          pct: kpis.turnoverSplit.casinoPct,
          colorClass: "bg-primary",
          tipDetail: `${money(kpis.casino.stake_kes)} · ${countShort(kpis.casino.bets)} bets`,
        },
        {
          label: "Sports",
          pct: kpis.turnoverSplit.sportsPct,
          colorClass: "bg-chart-sports",
          tipDetail: `${money(kpis.sports.stake_kes)} · ${countShort(kpis.sports.bets)} bets`,
        },
      ]
    : [];

  const deposits = (kpis?.deposits || []).map((amount, index) => ({
    label: `#${index + 1}`,
    value: amount,
    display: moneyExact(amount),
    tip: `Deposit #${index + 1} · ${moneyExact(amount)}`,
  }));

  return (
    <section>
      <SectionHeading
        id="pi-value"
        eyebrow="Section A"
        title="Value & P&L"
        description="Turnover, hold and gross gaming revenue over time — all figures operator-side."
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <InsightCard
          className="xl:col-span-8"
          title="Stake and GGR over time"
          subtitle="Bars: stake by product. Line: GGR."
          control={
            <SegmentedToggle
              ariaLabel="Chart period"
              options={GRAINS}
              value={grain}
              onChange={setGrain}
            />
          }
        >
          {loading ? <Skeleton className="h-[288px] w-full rounded-md" /> : <PnlChart series={series} grain={grain} />}
          {!loading && series.length > 0 && <ChartLegend />}
        </InsightCard>

        <div className="flex flex-col gap-6 xl:col-span-4">
          <InsightCard title="Casino vs sports" subtitle="Share of lifetime turnover">
            {loading ? (
              <Skeleton className="h-16 w-full rounded-md" />
            ) : !kpis || !kpis.turnover ? (
              <EmptyState minHeight={92} message="No turnover recorded yet." />
            ) : (
              <>
                {/* Legend rounds to whole points; the hover tooltip keeps the exact split. */}
                <ShareBar slices={split} height={12} legendValue={(slice) => pct(slice.pct, 0)} />
                <div className="mt-3 grid grid-cols-2 gap-3 border-t pt-3">
                  <div>
                    <div className="text-xs text-muted-foreground">Casino bets</div>
                    <div className="text-sm font-semibold tabular-nums text-foreground">
                      {countShort(kpis.casino.bets)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Sports bets</div>
                    <div className="text-sm font-semibold tabular-nums text-foreground">
                      {countShort(kpis.sports.bets)}
                    </div>
                  </div>
                </div>
              </>
            )}
          </InsightCard>

          <InsightCard title="Top 5 deposits" subtitle="Largest single deposits on record" className="flex-1">
            {loading ? (
              <Skeleton className="h-28 w-full rounded-md" />
            ) : !deposits.length ? (
              <EmptyState minHeight={112} message="No deposits recorded yet." />
            ) : (
              <BarList rows={deposits} labelWidth={22} valueWidth={78} colorClass="bg-primary" />
            )}
          </InsightCard>
        </div>
      </div>
    </section>
  );
}
